import type { ExperienceTrackerAPI } from '@atlassian/experience-tracker/ExperienceTracker';

import { ExperienceName } from './constants';
import { getRemixFeatureArea } from './remix-feature-area';

/**
 * A short registration window lets sibling embed effects register before a page
 * with fast-loading embeds is declared successful.
 */
export const REMIX_VIEW_SETTLE_DELAY_MS = 100;

/**
 * Classic view can replace its renderer while loading or after completion.
 * Allow a matching remount to reclaim pending registrations, and retain stopped
 * sessions briefly to prevent duplicate page results.
 */
export const REMIX_VIEW_SESSION_RETENTION_MS = 1_000;

export type RemixViewAttributes = Record<string, unknown> & {
	contentType?: string;
	featureArea?: string;
	pageMode?: string;
	product?: string;
	remixType?: string;
};

type EmbedOutcome = 'pending' | 'success' | 'fail';

type EmbedRegistration = {
	attributes: RemixViewAttributes;
	mounted: boolean;
	outcome: EmbedOutcome;
	loadOutcome: EmbedOutcome;
	renderIdentity?: string;
	timedOut?: boolean;
	releaseTimer?: ReturnType<typeof setTimeout>;
};

type PageSession = {
	embeds: Map<string, EmbedRegistration>;
	experienceTracker: ExperienceTrackerAPI;
	pageSessionId?: string;
	pageSessionKey: object;
	retentionTimer?: ReturnType<typeof setTimeout>;
	settleTimer?: ReturnType<typeof setTimeout>;
	stopped: boolean;
	loadTiming: {
		id: string;
		startedAt: number;
		startedAtEpochMs: number;
		stopped: boolean;
		readyAt?: number;
		settleTimer?: ReturnType<typeof setTimeout>;
	};
};

/**
 * The page session key is supplied by the host's shared experience-tracker
 * context. `useAIMateExperienceTracker()` returns a distinct Proxy per hook, so
 * using the reporting tracker itself as this key would incorrectly create one
 * session per embed.
 */
const sessions = new Map<object, PageSession>();

const getPageSessionKey = (
	experienceTracker: ExperienceTrackerAPI,
	pageSessionKey?: object,
): object => pageSessionKey ?? experienceTracker;

const clearSettleTimer = (session: PageSession): void => {
	if (session.settleTimer !== undefined) {
		clearTimeout(session.settleTimer);
		session.settleTimer = undefined;
	}
};

const clearRetentionTimer = (session: PageSession): void => {
	if (session.retentionTimer !== undefined) {
		clearTimeout(session.retentionTimer);
		session.retentionTimer = undefined;
	}
};

const deleteSession = (session: PageSession): void => {
	clearRetentionTimer(session);
	clearSettleTimer(session);
	clearTimeout(session.loadTiming.settleTimer);
	session.embeds.forEach(({ releaseTimer }) => clearTimeout(releaseTimer));
	if (sessions.get(session.pageSessionKey) === session) {
		sessions.delete(session.pageSessionKey);
	}
	if (sessions.size === 0 && typeof window !== 'undefined') {
		window.removeEventListener('pagehide', onBrowserPageHide);
	}
};

const distinctStrings = (values: Array<unknown>): string[] => [
	...new Set(
		values.filter((value): value is string => typeof value === 'string' && value.length > 0),
	),
];

const buildPageAttributes = (
	session: PageSession,
	terminalAttributes?: RemixViewAttributes,
): RemixViewAttributes => {
	const registrations = [...session.embeds.values()];
	const remixTypes = distinctStrings(registrations.map(({ attributes }) => attributes.remixType));
	const contentTypes = distinctStrings(
		registrations.map(({ attributes }) => attributes.contentType),
	);
	const pageModes = distinctStrings(registrations.map(({ attributes }) => attributes.pageMode));
	const product = registrations.find(({ attributes }) => attributes.product)?.attributes.product;
	const existingFeatureArea = registrations.find(({ attributes }) => attributes.featureArea)
		?.attributes.featureArea;
	const featureArea = getRemixFeatureArea({
		remixType:
			remixTypes.length === 1 ? remixTypes[0] : remixTypes.length > 1 ? 'mixed' : undefined,
		existingFeatureArea: terminalAttributes?.featureArea ?? existingFeatureArea,
	});

	return {
		...terminalAttributes,
		...(product ? { product } : {}),
		...(contentTypes.length === 1 ? { contentType: contentTypes[0] } : {}),
		...(contentTypes.length > 1 ? { contentType: 'mixed', contentTypes } : {}),
		...(pageModes.length === 1 ? { pageMode: pageModes[0] } : {}),
		...(pageModes.length > 1 ? { pageMode: 'mixed', pageModes } : {}),
		...(remixTypes.length === 1 ? { remixType: remixTypes[0] } : {}),
		...(remixTypes.length > 1 ? { remixType: 'mixed', remixTypes } : {}),
		featureArea,
		embedCount: registrations.length,
		loadAttemptId: session.loadTiming.id,
		viewTrackingMode: 'deadline',
	};
};

const maybeDeleteSession = (session: PageSession): void => {
	if ([...session.embeds.values()].every(({ mounted }) => !mounted)) {
		if (!session.loadTiming.stopped) {
			return;
		}
		if (!session.stopped && session.settleTimer !== undefined) {
			return;
		}
		if (session.stopped && session.pageSessionId !== undefined) {
			if (session.retentionTimer === undefined) {
				session.retentionTimer = setTimeout(() => {
					session.retentionTimer = undefined;
					deleteSession(session);
				}, REMIX_VIEW_SESSION_RETENTION_MS);
			}
			return;
		}
		deleteSession(session);
	}
};

const abortForNavigation = (
	session: PageSession,
	abortReason: 'navigation' | 'lifecycle_ended' = 'navigation',
): void => {
	finishLoad(session, 'abort', { abortReason });
	if (session.stopped) {
		return;
	}

	clearSettleTimer(session);
	session.stopped = true;
	try {
		session.experienceTracker.abort({
			name: ExperienceName.REMIX_VIEW_LIMITED,
			reason:
				abortReason === 'navigation'
					? 'User navigated away before Remix embeds finished rendering'
					: 'Remix embed lifecycle ended before rendering completed',
			attributes: buildPageAttributes(session, { abortReason }),
		});
	} catch {
		// Reliability instrumentation must never surface errors into the host product.
	}
};

const buildLoadAttributes = (session: PageSession, attributes?: RemixViewAttributes) => {
	const timing = session.loadTiming;
	const elapsedMs = Math.round((timing.readyAt ?? performance.now()) - timing.startedAt);
	return {
		...buildPageAttributes(session, attributes),
		loadAttemptId: timing.id,
		viewTrackingMode: 'eventual',
		loadStartedAtEpochMs: timing.startedAtEpochMs,
		loadElapsedMs: elapsedMs,
		timingOrigin: 'first-embed-registration',
		timedOutEmbedCount: [...session.embeds.values()].filter(({ timedOut }) => timedOut).length,
		pendingEmbedCount: [...session.embeds.values()].filter(
			({ loadOutcome }) => loadOutcome === 'pending',
		).length,
	};
};

const LOAD_TIME_BUCKETS = [
	[15_000, '0-15s'],
	[30_000, '15-30s'],
	[45_000, '30-45s'],
	[60_000, '45-60s'],
] as const;

const getLoadTimeBucket = (elapsedMs: number): string =>
	LOAD_TIME_BUCKETS.find(([upperBoundMs]) => elapsedMs < upperBoundMs)?.[1] ?? '60s+';

// This parallel experience has no deadline: timeout failures go to REMIX_VIEW_LIMITED,
// while this one measures the existing readiness signals until the lifecycle ends.
const finishLoad = (
	session: PageSession,
	outcome: 'success' | 'failure' | 'abort',
	attributes?: RemixViewAttributes,
	error?: Error,
): void => {
	const timing = session.loadTiming;
	if (timing.stopped) {
		return;
	}
	timing.stopped = true;
	clearTimeout(timing.settleTimer);
	const options = {
		name: ExperienceName.REMIX_VIEW,
		attributes: buildLoadAttributes(session, attributes),
	};
	try {
		if (outcome === 'success') {
			const elapsedMs = options.attributes.loadElapsedMs;
			session.experienceTracker.succeed({
				...options,
				attributes: {
					...options.attributes,
					loadTimeBucket: getLoadTimeBucket(elapsedMs),
				},
			});
		} else if (outcome === 'failure') {
			session.experienceTracker.fail({ ...options, error: error! });
		} else {
			session.experienceTracker.abort({
				...options,
				reason: 'Remix load lifecycle ended before all readiness signals were received',
			});
		}
	} catch {
		// Timing instrumentation must never interrupt rendering or the existing SLI.
	}
	maybeDeleteSession(session);
};

const scheduleLoadSuccess = (session: PageSession): void => {
	const timing = session.loadTiming;
	if (timing.stopped) {
		return;
	}
	clearTimeout(timing.settleTimer);
	if ([...session.embeds.values()].some(({ loadOutcome }) => loadOutcome !== 'success')) {
		return;
	}
	timing.readyAt = performance.now();
	timing.settleTimer = setTimeout(() => finishLoad(session, 'success'), REMIX_VIEW_SETTLE_DELAY_MS);
};

const getMatchingSession = (
	experienceTracker: ExperienceTrackerAPI,
	pageSessionKey?: object,
	pageSessionId?: string,
): PageSession | undefined => {
	const session = sessions.get(getPageSessionKey(experienceTracker, pageSessionKey));
	if (
		pageSessionId !== undefined &&
		session?.pageSessionId !== undefined &&
		session.pageSessionId !== pageSessionId
	) {
		return undefined;
	}
	return session;
};

const emitPageSuccess = (session: PageSession): void => {
	session.stopped = true;
	clearSettleTimer(session);
	try {
		session.experienceTracker.succeed({
			name: ExperienceName.REMIX_VIEW_LIMITED,
			attributes: buildPageAttributes(session),
		});
	} catch {
		// Reliability instrumentation must never surface errors into the host product.
	} finally {
		maybeDeleteSession(session);
	}
};

const schedulePageSuccess = (session: PageSession): void => {
	clearSettleTimer(session);
	if (
		session.stopped ||
		session.embeds.size === 0 ||
		[...session.embeds.values()].some(({ outcome }) => outcome === 'pending')
	) {
		return;
	}

	session.settleTimer = setTimeout(() => {
		session.settleTimer = undefined;
		if (
			session.stopped ||
			[...session.embeds.values()].some(({ outcome }) => outcome === 'pending')
		) {
			return;
		}

		emitPageSuccess(session);
	}, REMIX_VIEW_SETTLE_DELAY_MS);
};

/**
 * Ends and removes the session for content that has left the active route.
 * Same-page renderer unmounts do not call this, so they can still reclaim the
 * retained session without suppressing a later visit to the same content.
 */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file -- This lifecycle operation must share the module-private page session store.
export const retireRemixViewPageSession = ({
	experienceTracker,
	pageSessionKey,
	pageSessionId,
}: {
	experienceTracker: ExperienceTrackerAPI;
	pageSessionKey?: object;
	pageSessionId?: string;
}): void => {
	const session = getMatchingSession(experienceTracker, pageSessionKey, pageSessionId);
	if (!session) {
		return;
	}
	if (!session.loadTiming.stopped) {
		if ([...session.embeds.values()].every(({ loadOutcome }) => loadOutcome === 'success')) {
			finishLoad(session, 'success');
		} else {
			finishLoad(session, 'abort', { abortReason: 'navigation' });
		}
	}

	if (!session.stopped) {
		const hasPendingEmbed = [...session.embeds.values()].some(
			({ outcome }) => outcome === 'pending',
		);
		if (hasPendingEmbed) {
			abortForNavigation(session);
		} else {
			emitPageSuccess(session);
		}
	}

	deleteSession(session);
};

const onBrowserPageHide = (event: PageTransitionEvent): void => {
	// A cached document can resume with its existing mounted embeds and callbacks.
	if (event.persisted) {
		return;
	}
	for (const session of [...sessions.values()]) {
		retireRemixViewPageSession({
			experienceTracker: session.experienceTracker,
			pageSessionId: session.pageSessionId,
			pageSessionKey: session.pageSessionKey,
		});
	}
};

/** Clears all page state without emitting. Exposed for deterministic tests. */
export const resetRemixViewExperience = (): void => {
	sessions.forEach((session) => {
		clearRetentionTimer(session);
		clearSettleTimer(session);
		clearTimeout(session.loadTiming.settleTimer);
		session.embeds.forEach(({ releaseTimer }) => clearTimeout(releaseTimer));
	});
	sessions.clear();
	if (typeof window !== 'undefined') {
		window.removeEventListener('pagehide', onBrowserPageHide);
	}
};

/**
 * Registers one Remix render as part of the current page-level experience.
 * The first registration starts the parent; later registrations update its
 * final aggregate rather than starting competing experiences with the same name.
 */
export const beginRemixEmbedRender = ({
	attributes = {},
	embedId,
	experienceTracker,
	pageSessionKey,
	pageSessionId,
	renderIdentity,
}: {
	attributes?: RemixViewAttributes;
	embedId: string;
	experienceTracker: ExperienceTrackerAPI;
	pageSessionKey?: object;
	pageSessionId?: string;
	renderIdentity?: string;
}): void => {
	const resolvedPageSessionKey = getPageSessionKey(experienceTracker, pageSessionKey);
	let session = sessions.get(resolvedPageSessionKey);
	if (
		session &&
		pageSessionId !== undefined &&
		session.pageSessionId !== undefined &&
		session.pageSessionId !== pageSessionId
	) {
		abortForNavigation(session);
		deleteSession(session);
		session = undefined;
	}

	if (!session) {
		session = {
			embeds: new Map(),
			experienceTracker,
			pageSessionId,
			pageSessionKey: resolvedPageSessionKey,
			stopped: false,
			loadTiming: {
				id: crypto.randomUUID(),
				startedAt: performance.now(),
				startedAtEpochMs: Date.now(),
				stopped: false,
			},
		};
		sessions.set(resolvedPageSessionKey, session);
		if (typeof window !== 'undefined') {
			window.addEventListener('pagehide', onBrowserPageHide);
		}
	} else if (session.pageSessionId === undefined && pageSessionId !== undefined) {
		session.pageSessionId = pageSessionId;
	}

	if (session.stopped && session.loadTiming.stopped) {
		return;
	}

	let existing = session.embeds.get(embedId);
	// Media instances and registration order can change when the renderer is replaced.
	if (!existing && renderIdentity !== undefined) {
		const released = [...session.embeds.entries()].find(
			([, registration]) => !registration.mounted && registration.renderIdentity === renderIdentity,
		);
		if (released) {
			const [releasedId, registration] = released;
			session.embeds.delete(releasedId);
			existing = registration;
		}
	}
	clearRetentionTimer(session);
	clearTimeout(existing?.releaseTimer);
	if (!existing) {
		clearSettleTimer(session);
		clearTimeout(session.loadTiming.settleTimer);
		session.loadTiming.readyAt = undefined;
	}
	session.embeds.set(embedId, {
		attributes: { ...existing?.attributes, ...attributes },
		mounted: true,
		outcome: existing?.outcome ?? 'pending',
		loadOutcome: existing?.loadOutcome ?? 'pending',
		renderIdentity,
		timedOut: existing?.timedOut,
	});
	if (!session.loadTiming.stopped) {
		const options = {
			name: ExperienceName.REMIX_VIEW,
			attributes: buildLoadAttributes(session),
		};
		try {
			if (session.embeds.size === 1 && !existing) {
				experienceTracker.start({ ...options, id: session.loadTiming.id });
			} else {
				experienceTracker.updateAttributes(options);
			}
		} catch {
			// Timing instrumentation must not prevent the existing SLI from starting.
		}
	}
	if (session.stopped) {
		return;
	}

	if (session.embeds.size === 1 && !existing) {
		experienceTracker.start({
			name: ExperienceName.REMIX_VIEW_LIMITED,
			id: `${session.loadTiming.id}:limited`,
			attributes: buildPageAttributes(session),
		});
		return;
	}

	experienceTracker.updateAttributes({
		name: ExperienceName.REMIX_VIEW_LIMITED,
		attributes: buildPageAttributes(session),
	});
};

/** Marks one registered embed successful and completes the page once all are successful. */
export const completeRemixEmbedRender = ({
	embedId,
	experienceTracker,
	pageSessionKey,
	pageSessionId,
}: {
	embedId: string;
	experienceTracker: ExperienceTrackerAPI;
	pageSessionKey?: object;
	pageSessionId?: string;
}): void => {
	const session = getMatchingSession(experienceTracker, pageSessionKey, pageSessionId);
	const registration = session?.embeds.get(embedId);
	if (!registration?.mounted) {
		return;
	}
	if (session && !session.loadTiming.stopped && registration?.loadOutcome === 'pending') {
		registration.loadOutcome = 'success';
		scheduleLoadSuccess(session);
	}
	if (!session || session.stopped || !registration || registration.outcome !== 'pending') {
		return;
	}

	registration.outcome = 'success';
	schedulePageSuccess(session);
};

/** Fails the page-level experience as soon as any registered Remix embed fails. */
export const failRemixEmbedRender = ({
	attributes,
	embedId,
	error,
	errorType,
	experienceTracker,
	pageSessionKey,
	pageSessionId,
}: {
	attributes?: RemixViewAttributes;
	embedId: string;
	error: Error;
	errorType?: string;
	experienceTracker: ExperienceTrackerAPI;
	pageSessionKey?: object;
	pageSessionId?: string;
}): void => {
	const session = getMatchingSession(experienceTracker, pageSessionKey, pageSessionId);
	const registration = session?.embeds.get(embedId);
	if (!registration?.mounted) {
		return;
	}
	if (session && !session.loadTiming.stopped && registration?.loadOutcome === 'pending') {
		if (errorType === 'render_timeout') {
			registration.timedOut = true;
		} else {
			registration.loadOutcome = 'fail';
			finishLoad(session, 'failure', { ...attributes, errorType }, error);
		}
	}
	if (!session || session.stopped || !registration || registration.outcome !== 'pending') {
		return;
	}

	clearSettleTimer(session);
	registration.attributes = {
		...registration.attributes,
		...attributes,
		...(errorType ? { errorType } : {}),
	};
	registration.outcome = 'fail';
	session.stopped = true;

	try {
		experienceTracker.fail({
			name: ExperienceName.REMIX_VIEW_LIMITED,
			error,
			attributes: buildPageAttributes(session, registration.attributes),
		});
	} catch {
		// Reliability instrumentation must never surface errors into the host product.
	} finally {
		maybeDeleteSession(session);
	}
};

/**
 * Deregisters an embed. Content-scoped registrations can be reclaimed during a
 * renderer replacement. Confirmed route departures retire the session directly.
 */
export const releaseRemixEmbedRender = ({
	embedId,
	experienceTracker,
	pageSessionKey,
	pageSessionId,
}: {
	embedId: string;
	experienceTracker: ExperienceTrackerAPI;
	pageSessionKey?: object;
	pageSessionId?: string;
}): void => {
	const session = getMatchingSession(experienceTracker, pageSessionKey, pageSessionId);
	const registration = session?.embeds.get(embedId);
	if (!session || !registration) {
		return;
	}

	registration.mounted = false;
	const hasPendingOutcome =
		(!session.loadTiming.stopped && registration.loadOutcome === 'pending') ||
		(!session.stopped && registration.outcome === 'pending');
	if (hasPendingOutcome) {
		if (session.pageSessionId === undefined) {
			abortForNavigation(session);
		} else if (registration.releaseTimer === undefined) {
			registration.releaseTimer = setTimeout(() => {
				registration.releaseTimer = undefined;
				if (!registration.mounted && sessions.get(session.pageSessionKey) === session) {
					abortForNavigation(session, 'lifecycle_ended');
					maybeDeleteSession(session);
				}
			}, REMIX_VIEW_SESSION_RETENTION_MS);
		}
	}
	maybeDeleteSession(session);
};
